// components/common/Pagination.jsx
// Client-side paging for the data tables, plus a debounce hook for the
// search boxes that feed them.
import React, { useState, useMemo, useEffect, useRef } from "react";
import { Button, Select, Text } from "@fluentui/react-components";
import {
  ChevronLeft24Regular, ChevronRight24Regular,
  ChevronDoubleLeft20Regular, ChevronDoubleRight20Regular,
} from "@fluentui/react-icons";

export const PAGE_SIZES = [25, 50, 100, 250];

/**
 * Slice a list into pages.
 *
 * `resetKey` should carry whatever the caller filters or sorts by — when it
 * changes the view jumps back to page one, because staying on page 7 of a
 * result set that just shrank to two pages shows the user an empty table.
 */
export function usePagination(items, { defaultPageSize = 50, resetKey = "" } = {}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => { setPage(1); }, [resetKey, pageSize]);

  // Guard against the list shrinking beneath the current page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;

  const pageItems = useMemo(
    () => items.slice(start, start + pageSize),
    [items, start, pageSize],
  );

  return {
    page: safePage,
    setPage,
    pageSize,
    setPageSize,
    pageItems,
    totalPages,
    total,
    from: total === 0 ? 0 : start + 1,
    to: Math.min(start + pageSize, total),
    isPaged: total > pageSize,
  };
}

/** Debounce a rapidly changing value — search text, a rate being typed. */
export function useDebounced(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/** Run an expensive render only once the browser is idle, so typing stays smooth. */
export function useDeferredList(items) {
  const [deferred, setDeferred] = useState(items);
  const raf = useRef(null);
  useEffect(() => {
    if (raf.current) cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => setDeferred(items));
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [items]);
  return deferred;
}

function pageWindow(page, totalPages, span = 5) {
  if (totalPages <= span) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const half = Math.floor(span / 2);
  let start = Math.max(1, page - half);
  const end = Math.min(totalPages, start + span - 1);
  start = Math.max(1, end - span + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export function Pagination({
  page, setPage, pageSize, setPageSize, totalPages, total, from, to,
  accent = "#A66400", noun = "record", pageSizes = PAGE_SIZES,
}) {
  if (total === 0) return null;
  const pages = pageWindow(page, totalPages);
  const single = totalPages === 1;

  const navBtn = (disabled) => ({
    minWidth: 30, height: 30, padding: 0,
    color: disabled ? "#CCC" : "#555",
  });

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 14, flexWrap: "wrap", padding: "11px 16px",
      borderTop: "1px solid #EEF0F4", background: "#FAFBFC",
    }}>
      <Text style={{ fontSize: 12, color: "#888", whiteSpace: "nowrap" }}>
        Showing <strong style={{ color: "#333" }}>{from.toLocaleString()}–{to.toLocaleString()}</strong>
        {" "}of <strong style={{ color: "#333" }}>{total.toLocaleString()}</strong> {noun}{total === 1 ? "" : "s"}
      </Text>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Text style={{ fontSize: 11.5, color: "#888", whiteSpace: "nowrap" }}>Rows</Text>
          <Select
            value={String(pageSize)}
            onChange={(_, d) => setPageSize(Number(d.value))}
            style={{ minWidth: 74 }}
            aria-label="Rows per page"
          >
            {pageSizes.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </div>

        {!single && (
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <Button appearance="subtle" size="small" icon={<ChevronDoubleLeft20Regular />}
              onClick={() => setPage(1)} disabled={page === 1}
              style={navBtn(page === 1)} aria-label="First page" />
            <Button appearance="subtle" size="small" icon={<ChevronLeft24Regular />}
              onClick={() => setPage(page - 1)} disabled={page === 1}
              style={navBtn(page === 1)} aria-label="Previous page" />

            {pages[0] > 1 && <span style={{ color: "#BBB", fontSize: 12, padding: "0 3px" }}>…</span>}

            {pages.map((p) => {
              const on = p === page;
              return (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  aria-current={on ? "page" : undefined}
                  style={{
                    minWidth: 30, height: 30, borderRadius: 7, cursor: "pointer",
                    border: `1px solid ${on ? accent : "transparent"}`,
                    background: on ? accent : "transparent",
                    color: on ? "#fff" : "#555",
                    fontWeight: on ? 700 : 400, fontSize: 12.5,
                    fontFamily: "inherit", fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {p}
                </button>
              );
            })}

            {pages[pages.length - 1] < totalPages && (
              <span style={{ color: "#BBB", fontSize: 12, padding: "0 3px" }}>…</span>
            )}

            <Button appearance="subtle" size="small" icon={<ChevronRight24Regular />}
              onClick={() => setPage(page + 1)} disabled={page === totalPages}
              style={navBtn(page === totalPages)} aria-label="Next page" />
            <Button appearance="subtle" size="small" icon={<ChevronDoubleRight20Regular />}
              onClick={() => setPage(totalPages)} disabled={page === totalPages}
              style={navBtn(page === totalPages)} aria-label="Last page" />
          </div>
        )}
      </div>
    </div>
  );
}
